import { LowerCasePipe } from '@angular/common';
import { Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';

import { DialogComponent } from '../../../../core/components/dialog/dialog.component';
import { AlertService } from '../../../../core/services/alert.service';
import { ConfirmationService } from '../../../../core/services/confirmation.service';
import { GoogleMapsService } from '../../../../core/services/google-maps-loader.service';
import { ShippingStopKindEnum } from '../../enums/shipping-stop-kind.enum';
import { IShippingStop } from '../../interfaces/shipping-stop.interface';
import { ActivatedRoute } from '@angular/router';
import { LinkToMapPipe } from '../../pipes/link-to-map.pipe';
import { ShippingStopKindPipe } from '../../pipes/shipping-stop-kind.pipe';
import { IProofUploadResponse, ProofEvidenceService, ShippingCarrier } from '../../services/proof-evidence.service';
import { validateProofPhoto, validateReceiverName } from '../../validators/proof-validators';

@Component({
  selector: 'peca-shipping-stop',
  templateUrl: './shipping-stop.component.html',
  styleUrl: './shipping-stop.component.scss',
  imports: [LinkToMapPipe, ShippingStopKindPipe, LowerCasePipe, DialogComponent, FormsModule],
})
export class ShippingStopComponent implements OnInit, OnDestroy {
  @ViewChild('dialog')
  dialog!: DialogComponent;

  @Input({ required: true })
  stop!: IShippingStop;

  @Input()
  carrier: ShippingCarrier = 'self-shipping';

  @Output()
  finalize = new EventEmitter<IShippingStop>();

  confirmationService = inject(ConfirmationService);
  googleMapsService = inject(GoogleMapsService);
  alertService = inject(AlertService);
  proofEvidenceService = inject(ProofEvidenceService);
  activatedRoute = inject(ActivatedRoute);

  selectedPhoto: File | null = null;
  previewUrl: string | null = null;
  receiverName = '';
  photoError: string | null = null;
  receiverError: string | null = null;
  submitting = false;
  proofUrl: string | null = null;

  ngOnInit() {
    if (this.stop.finalized && this.stop.proofUploadId) {
      this.proofUrl = this.proofEvidenceService.proofContentUrl(this.stop.proofUploadId);
    }
  }

  ngOnDestroy() {
    if (this.previewUrl) {
      URL.revokeObjectURL(this.previewUrl);
    }
  }

  get isDropoff(): boolean {
    return this.stop.kind === ShippingStopKindEnum.DROPOFF;
  }

  async copyAddress(address: string) {
    this.dialog.close();
    await navigator.clipboard.writeText(address);
    this.alertService.confirm(['O endereço foi copiado para sua área de transferência']);
  }

  viewOnMap(index: number, event: Event) {
    event.preventDefault();

    const currentStops = this.googleMapsService.stops();
    const targetStop = currentStops[index];

    if (targetStop) {
      this.googleMapsService.openMarker(targetStop);
      document.querySelector('peca-google-maps')?.scrollIntoView({ behavior: 'smooth' });
    }
  }

  onClickDetails() {
    this.dialog.open();
  }

  onClickOpenAtMap() {
    this.dialog.close();
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    this.photoError = null;

    if (!file) {
      return;
    }

    const error = validateProofPhoto(file);
    if (error) {
      this.photoError = error;
      input.value = '';
      return;
    }

    if (this.previewUrl) {
      URL.revokeObjectURL(this.previewUrl);
    }
    this.selectedPhoto = file;
    this.previewUrl = URL.createObjectURL(file);
  }

  onClickCheckStop() {
    if (this.stop.finalized) {
      return;
    }
    this.photoError = null;
    this.receiverError = null;
    this.dialog.open();
  }

  onSubmitProof() {
    if (this.stop.finalized || this.submitting) {
      return;
    }

    if (!this.selectedPhoto) {
      this.photoError = 'A foto do comprovante é obrigatória.';
    }

    this.receiverError = null;
    if (this.isDropoff) {
      this.receiverError = validateReceiverName(this.receiverName);
    }

    if (this.photoError || this.receiverError) {
      return;
    }

    const confirmation$ = this.confirmationService.confirm({
      confirmation: ['Você está finalizando a parada em:', this.stop.address, 'Deseja continuar?'],
      deniable: true,
      revertable: false,
    });

    confirmation$.subscribe((confirm) => {
      if (!confirm) {
        return;
      }
      void this.uploadAndFinalize();
    });
  }

  private async uploadAndFinalize() {
    if (!this.selectedPhoto) {
      return;
    }
    this.submitting = true;
    this.photoError = null;

    try {
      const shippingReference = this.resolveShippingReference();
      const { uploadId } = await new Promise<IProofUploadResponse>((resolve, reject) => {
        this.proofEvidenceService.uploadProof(this.carrier, shippingReference, this.stop.index, this.selectedPhoto as File).subscribe({
          next: resolve,
          error: reject,
        });
      });

      this.stop.proofUploadId = uploadId;
      if (this.isDropoff) {
        this.stop.receiverName = this.receiverName.trim();
      }
      this.dialog.close();
      this.finalize.emit(this.stop);
    } catch {
      this.photoError = 'Não foi possível enviar a foto. Tente novamente.';
    } finally {
      this.submitting = false;
    }
  }

  private resolveShippingReference(): string {
    return this.activatedRoute.snapshot.paramMap.get('shippingReference') ?? '';
  }
}
